import { useEffect, useRef } from 'react';
import { getResolvedExtrasPrices } from '@koob/utils';
import {
  getInitialExperienceFilters,
  getPricingRequestFilters,
} from '../../logic/experienceDayPricing';
import { useExperiencePricingQuery } from '../../hooks/useExperiencePricingQuery';
import { useExperienceNestedField } from '../../hooks/useTripDataFields';
import { useExperiencePricingConfig } from '../../hooks/useExperiencePricingConfig';
import { useDay } from '../../hooks/useTripStoreSelectors';
import useTripStore from '../../store/useTripStore';

interface PriceRequestParams {
  dayIndex: number;
  date: unknown;
  experience: any;
  setExperience: (experience: any) => void;
  tripFilters: any;
}

// Fetches this instance's pricing and keeps the day's `priceLoading` flag in
// step with the request. Also stamps the experience with the filters it was
// first drawn under, once.
//
// An app that stores the raw response on the day (`storesPricingResponse`,
// DMC) reuses it instead of asking again while the dates it was priced for
// still hold: `priceObject` reads the stored response when no fresh one is
// there, and every fresh one is written back for the next mount.
export function useTripDayExperiencePriceRequest({
  dayIndex,
  date,
  experience,
  setExperience,
  tripFilters,
}: PriceRequestParams) {
  const [{ value: priceLoading }, , { setValue: setPriceLoading }] =
    useExperienceNestedField(dayIndex, 'priceLoading');
  const day = useDay(dayIndex);
  const updateDay = useTripStore((state) => state.updateDay);
  const { getExperiencePricingFilters, storesPricingResponse, locale } =
    useExperiencePricingConfig();

  // Track experience IDs for which we've already set filters to prevent loops
  const filtersInitializedFor = useRef(new Set<string>());

  const { filters, hasValidDate } = getPricingRequestFilters({
    experience,
    date,
    composition: tripFilters?.composition,
    templateId: tripFilters?.templateId,
    extraFilters: getExperiencePricingFilters?.(tripFilters),
  });

  // Only the instance's first day stores or reuses a response: it is the one
  // that carries the program's pricing.
  const isFirstDay = !experience?.dayIndex || experience.dayIndex === 1;
  const canReuseStored =
    storesPricingResponse &&
    isFirstDay &&
    !!day?.experiencePricing?.extras &&
    day.experiencePricingStartAt === filters.startAt;

  const {
    data: priceData,
    isLoading: isLoadingPrice,
    isPreviousData,
  } = useExperiencePricingQuery({
    filters,
    locale: tripFilters?.locale ?? locale ?? 'en',
    enabled: !!experience?.id && hasValidDate && !canReuseStored,
  });

  const fetchedPrice = priceData?.data?.[0]?.price;
  const priceObject =
    fetchedPrice ?? (storesPricingResponse ? day?.experiencePricing : null);
  // The API only returns `minPax` when the trip's travellers are below it.
  const requiredMinPax = priceData?.data?.[0]?.minPax;

  // KD-3749 — a response that came back is an answer even when it prices
  // nothing: the experience itself has no price on these dates, and none of
  // its services can be used either. No fresh response yet falls back to a
  // stored pricing (DMC only): one saved without its extras says nothing
  // about them.
  const extrasPrices = priceData?.data
    ? (getResolvedExtrasPrices(fetchedPrice) ?? [])
    : day?.experiencePricing?.extras;

  useEffect(() => {
    if (
      storesPricingResponse &&
      fetchedPrice &&
      !isPreviousData &&
      experience?.id &&
      isFirstDay &&
      day?.experiencePricingStartAt !== filters.startAt
    ) {
      updateDay(dayIndex, {
        experiencePricing: fetchedPrice,
        experiencePricingStartAt: filters.startAt,
      });
    }
  }, [
    storesPricingResponse,
    fetchedPrice,
    isPreviousData,
    experience?.id,
    isFirstDay,
    day?.experiencePricingStartAt,
    filters.startAt,
    dayIndex,
    updateDay,
  ]);

  useEffect(() => {
    if (experience?.id) {
      // Sync loading state with API loading state
      setPriceLoading(isLoadingPrice);
    } else {
      // Reset loading state when experience is removed
      setPriceLoading(false);
    }
  }, [experience?.id, isLoadingPrice, setPriceLoading]);

  useEffect(() => {
    const experienceId = experience?.id;
    if (
      experienceId &&
      hasValidDate &&
      !experience.filters &&
      !filtersInitializedFor.current.has(experienceId)
    ) {
      filtersInitializedFor.current.add(experienceId);
      setExperience({
        ...experience,
        filters: getInitialExperienceFilters({
          experience,
          date,
          composition: tripFilters?.composition,
          templateId: tripFilters?.templateId,
        }),
      });
    }
    // Initializes once per experience id (ref guard): `experience` is read
    // whole, and `hasValidDate` follows `date`, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    experience?.id,
    experience?.filters,
    experience?.durationDays,
    date,
    tripFilters?.composition,
    tripFilters?.templateId,
    setExperience,
  ]);

  return {
    priceObject,
    priceLoading: priceLoading as boolean,
    requiredMinPax,
    extrasPrices,
  };
}
